import React from 'react';
import { useDesignMode } from '../../contexts/DesignContext';
import { classNames } from '../../utils/classNames';
import { sketchTiltStyle, SKETCH_TILT_BUTTON_MAX } from '../../utils/sketchTilt';

interface SketchButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'ghost';
  /** Stable seed for hand-drawn micro-tilt (defaults to button label / useId). */
  tiltSeed?: string | number;
}

export const SketchButton = React.forwardRef<HTMLButtonElement, SketchButtonProps>(
  (
    {
      className,
      variant = 'default',
      type = 'button',
      children,
      tiltSeed,
      style,
      ...props
    },
    ref
  ) => {
    const { isSketchy } = useDesignMode();
    const autoId = React.useId();
    const seed =
      tiltSeed ??
      (typeof children === 'string' ? children : undefined) ??
      autoId;

    return (
      <button
        ref={ref}
        type={type}
        className={classNames(
          isSketchy
            ? classNames(
                'sketch-btn px-4 py-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
                variant === 'primary' && 'sketch-btn-primary',
                variant === 'ghost' && 'shadow-none border-dashed'
              )
            : classNames(
                variant === 'primary' && 'btn-primary',
                variant === 'default' &&
                  'px-4 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-hover)] transition-colors',
                variant === 'ghost' &&
                  'px-4 py-2 rounded-lg text-[var(--color-text)] hover:bg-[var(--color-hover)] transition-colors'
              ),
          className
        )}
        style={isSketchy ? { ...sketchTiltStyle(seed, SKETCH_TILT_BUTTON_MAX), ...style } : style}
        {...props}
      >
        {children}
      </button>
    );
  }
);

SketchButton.displayName = 'SketchButton';
